import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Practitioner } from '@customTypes/Practitioner'
import { analyticsEventSelectedPractitioner } from 'src/services/analytics'
import Spinner from '@components/Spinner'
import Footer from '@components/Footer'
import Header from '@components/Header'
import Page from '@components/Page'
import Error from '@components/Error'
import { useChoiceState } from '@context/ChoiceContext'
import Body from './components/Body'
import { isEmpty } from 'lodash'
import { Button } from '@components/Button/Button'
import { usePractitionersContext } from '@context/PractitionersContext'
import { useGetTreatmentTitleWithDuration } from '@hooks/useGetTreatmentTitleWithDuration'
import { useVerifyPhoneContext } from '@context/VerifyPhoneContext'
import { TEST_IDS } from 'src/constants/testIds'
interface Props {
  practitioners?: Practitioner[]
  loading?: boolean
  error?: string
  onBackClicked: () => void
  onNextClicked: (selectedPractitionerIds: string[]) => void
  enableSoonestAvailablePractitioner?: boolean
  nonModalEmbed?: boolean
}

export const PractitionerScreen = ({ practitioners = [], loading, onBackClicked, onNextClicked }: Props) => {
  const { t } = useTranslation()
  const { preferredPractitionerReferenceIds, treatmentBundleTypeReferenceId, patientType } = useChoiceState()
  const { selectedPractitionersFor } = usePractitionersContext()
  const { selectedPatient } = useVerifyPhoneContext()
  const [selectedPractitionerIds, setSelectedPractitionerIds] = useState<string[]>([])
  const allPractitionersSelected = practitioners.length === selectedPractitionerIds.length
  const patientMadeSelection =
    selectedPractitionersFor.patientType === patientType &&
    selectedPractitionersFor.treatmentReferenceId === treatmentBundleTypeReferenceId &&
    selectedPractitionersFor.patientReferenceId === (selectedPatient?.patientReferenceId || '')
  const treatmentTitle = useGetTreatmentTitleWithDuration()

  useEffect(() => {
    if (patientMadeSelection) {
      setSelectedPractitionerIds(preferredPractitionerReferenceIds)
    }
  }, [preferredPractitionerReferenceIds, patientMadeSelection])

  const handleSelect = useCallback(
    (id: string) => {
      if (selectedPractitionerIds.includes(id)) {
        const filteredPractitioners = selectedPractitionerIds.filter((preferredId) => preferredId !== id)
        setSelectedPractitionerIds(filteredPractitioners)
      } else {
        setSelectedPractitionerIds([...selectedPractitionerIds, id])
        const selectedPractitioner = practitioners.find(({ referenceId }) => referenceId === id)
        analyticsEventSelectedPractitioner(selectedPractitioner?.name ?? '')
      }
    },
    [practitioners, selectedPractitionerIds, setSelectedPractitionerIds]
  )

  const handleSelectAll = () => {
    setSelectedPractitionerIds(practitioners.map((practitioner) => practitioner.referenceId || ''))
  }

  const handleToggleSelectAll = () => {
    if (allPractitionersSelected) {
      setSelectedPractitionerIds([])
    } else {
      handleSelectAll()
    }
  }

  return (
    <Page
      header={
        <Header
          onBackClick={onBackClicked}
          desktopHeadingText={t('practitioner_page_desktop_headline')}
          mobileHeadingText={t('practitioner_page_mobile_headline')}
          treatmentTitle={treatmentTitle}
        />
      }
      body={
        <>
          {loading && <Spinner center />}
          {!loading &&
            (practitioners.length ? (
              <Body
                selected={selectedPractitionerIds}
                clickHandler={handleSelect}
                practitioners={practitioners}
                allPractitionersSelected={allPractitionersSelected}
                handleToggleSelectAll={handleToggleSelectAll}
              />
            ) : (
              <Error />
            ))}
        </>
      }
      footer={
        <Footer
          button={
            <Button
              disabled={isEmpty(selectedPractitionerIds)}
              fullSize
              type="button"
              onClick={() => onNextClicked(selectedPractitionerIds)}
              data-test={TEST_IDS.nextButton}
              text={t('next')}
            />
          }
        />
      }
    />
  )
}
